import { Container, Sprite, Texture } from "pixi.js";

import { WAKE } from "../../../shared/Config";
import { WakeSettings } from "../../../shared/Settings";

/**
 * The dials, read once at module load like every other setting — see design/Settings.md.
 * `budget` is floored to a whole number because it sizes an array.
 */
let SPACING = WAKE.spacing;
let LIFE = WAKE.life;
let WIDTH_START = WAKE.widthStart;
let WIDTH_END = WAKE.widthEnd;
let OPACITY = WAKE.opacity;
let STERN_OFFSET = WAKE.sternOffset;
let ARM_SPREAD = WAKE.armSpread;
let SPREAD_RATE = WAKE.spreadRate;
let BOOST_WIDTH = WAKE.boostWidth;
let BOOST_OPACITY = WAKE.boostOpacity;
let DISPLACEMENT = WAKE.displacement;
let BUDGET = Math.floor(WAKE.budget);

/**
 * Puts new numbers behind the dials above.
 *
 * **For the dashboard's workspace and nothing else**, exactly as `setWaveDials` is — see the note
 * there. The trail is a picture of a course the arena has already decided, so two clients drawing
 * different foam is a difference of opinion about water rather than a desync.
 *
 * Everything a mark is *laid* with is read at the moment it is laid, so a change lands on the next
 * mark and the trail already on the water keeps the shape it was drawn with. Two things need more
 * than that: the size of the pool and whether a mark carries a lens at all, which is `restock`,
 * and how hard the sea reads the trail, which is `Water.setTrail` again.
 */
export function setWakeDials(values: WakeSettings): void {
    SPACING = values.spacing;
    LIFE = values.life;
    WIDTH_START = values.widthStart;
    WIDTH_END = values.widthEnd;
    OPACITY = values.opacity;
    STERN_OFFSET = values.sternOffset;
    ARM_SPREAD = values.armSpread;
    SPREAD_RATE = values.spreadRate;
    BOOST_WIDTH = values.boostWidth;
    BOOST_OPACITY = values.boostOpacity;
    DISPLACEMENT = values.displacement;
    BUDGET = Math.floor(values.budget);
    CULL_MARGIN = WIDTH_END * LENS_SPREAD;
}

/**
 * How much one mark adds to the foam channel at full strength. Well under 1 so that the turbo's
 * multiplier has somewhere to go, and so that foam is something a **run** of marks makes rather
 * than something one mark is: the channel accumulates, and [OceanFilter.ts](OceanFilter.ts) then cuts
 * foam out of it at a threshold. Where a thinning trail drops below that threshold is where the
 * white ends and plain disturbed water carries on.
 */
const FOAM_WEIGHT = 0.55;
/**
 * How much longer than it is wide a mark is drawn, along the direction of travel. Marks are
 * laid at a fixed **distance** apart, so stretching them along the track is what turns a row
 * of dots into a line without paying for more of them.
 */
const ELONGATION = 1.45;
/**
 * The curve a mark's strength follows over its life, as an exponent on the remaining fraction.
 * Above 1, so a mark is at its hardest in the first moments and then lets go — a linear fade
 * leaves the whole trail at one flat value and the tail never really disappears, it just stops.
 */
const FADE_POWER = 1.6;
/**
 * How many `spacing`s a hull may be behind its own track before the gap is read as a teleport
 * rather than as a fast frame. Beyond this the track is picked up at the new position and
 * nothing is laid across the gap — a respawn on the far side of the arena must not draw a line
 * to where the ship used to be.
 */
const MAX_CATCHUP = 12;
/**
 * How far a mark is scattered off the point it was laid at, as a fraction of its fresh width,
 * and how much its size is rolled either side of the dialled one.
 *
 * Without these the two arms are ruler-straight lines with a hard edge, which reads as a vapour
 * trail rather than as churned water. The scatter is the difference between a drawn line and
 * broken foam, and it costs one roll per mark. See design/Pitch.md on the art being chunky.
 */
const JITTER = 0.15;
const SIZE_VARIANCE = 0.14;

/**
 * The two arms of the V, as multipliers on the offset across the track. Hoisted so that laying
 * a pair allocates nothing — a battle lays thousands of them.
 */
const SIDES = [-1, 1];

/** Size of the generated foam texture, in pixels. Square, and scaled to `width` on use. */
const BLOB_SIZE = 128;
/**
 * The softness of that blob's edge, as an exponent on the distance from its centre, and how
 * many stops the gradient is built from. Above 1 keeps a strong core with a long tail, which is
 * what lets neighbouring marks add up into a continuous line instead of a string of discs.
 */
const BLOB_FALLOFF = 2.2;
const BLOB_STOPS = 24;

/**
 * How much broader the water a mark pushes around is than the foam that marks it, and how the
 * bump's slope is shaped across that radius.
 *
 * Wider, because the disturbance in the water is bigger than the white of it — foam is the
 * visible part of something that reaches further. The slope is a `sin` arch: flat in the middle
 * of the mark, steepest half way out, flat again at the rim, which is a lens rather than a step
 * and is what keeps the water bending instead of tearing.
 */
const LENS_SPREAD = 1.7;
/** Size of the generated lens texture, in pixels. Bigger than the foam blob: it is a gradient. */
const LENS_SIZE = 96;
/** How the lens fades into the neutral background at its rim, as an exponent. See `lensTexture`. */
const LENS_FEATHER = 1.5;

/** How far outside the viewport a mark is still drawn, in world pixels. The wider of the two. */
let CULL_MARGIN = WIDTH_END * LENS_SPREAD;

/** The blob, built once and shared by every mark on every sea. See `foamTexture`. */
let foam: Texture | null = null;
/** The lens, likewise. See `lensTexture`. */
let lens: Texture | null = null;

/**
 * A soft round blob, as a texture. Drawn on a 2D canvas rather than with `Graphics` because what
 * matters here is the *falloff curve* — the ramp from the centre out is the whole reason marks
 * merge into a line rather than beading — and a radial gradient states that curve directly.
 *
 * White, and tinted on use. It ends up in the **blue** channel of the trail map, where it is a
 * quantity rather than a colour: how much foam is on this pixel. What that quantity then looks
 * like is [OceanFilter.ts](OceanFilter.ts)'s business.
 */
function foamTexture(): Texture {
    if (foam) return foam;

    const canvas = document.createElement("canvas");
    canvas.width = BLOB_SIZE;
    canvas.height = BLOB_SIZE;

    const context = canvas.getContext("2d")!;
    const radius = BLOB_SIZE / 2;
    const gradient = context.createRadialGradient(radius, radius, 0, radius, radius, radius);

    for (let stop = 0; stop <= BLOB_STOPS; stop++) {
        const distance = stop / BLOB_STOPS;
        gradient.addColorStop(distance, `rgba(255, 255, 255, ${(1 - distance) ** BLOB_FALLOFF})`);
    }

    context.fillStyle = gradient;
    context.fillRect(0, 0, BLOB_SIZE, BLOB_SIZE);

    foam = Texture.from(canvas);
    return foam;
}

/**
 * A round bump, as a **displacement map**: red and green encode which way the water is pushed at
 * each pixel, `0.5` being not at all — the mapping [OceanFilter.ts](OceanFilter.ts) reads, and the
 * same one both of PixiJS's own displacement shaders use.
 *
 * Every pixel is pushed **outward from the centre**, by an amount that arches up and back down
 * across the radius, so one mark is a lens in the water rather than a hole in it. The alpha
 * feathers to nothing at the rim so that overlapping marks blend into each other and into the
 * neutral field behind them; without it each mark would cut a visible circle out of the sea.
 *
 * **Blue is zero here, not neutral.** These are drawn with ordinary blending, so whatever they
 * carry in that channel replaces what is under them — and that channel is the foam. Zero is safe
 * only because the foam is laid on top of the lenses afterwards, and additively.
 *
 * Built pixel by pixel rather than as a gradient, because there is no gradient that states "point
 * away from here" — the direction is different at every pixel, and only the arch is shared.
 */
function lensTexture(): Texture {
    if (lens) return lens;

    const canvas = document.createElement("canvas");
    canvas.width = LENS_SIZE;
    canvas.height = LENS_SIZE;

    const context = canvas.getContext("2d")!;
    const image = context.createImageData(LENS_SIZE, LENS_SIZE);
    const radius = LENS_SIZE / 2;

    for (let y = 0; y < LENS_SIZE; y++) {
        for (let x = 0; x < LENS_SIZE; x++) {
            const at = (y * LENS_SIZE + x) * 4;
            const acrossX = x + 0.5 - radius;
            const acrossY = y + 0.5 - radius;
            const spread = Math.hypot(acrossX, acrossY) / radius;

            if (spread >= 1) {
                // Outside the circle: neutral and invisible, so the corners of the square
                // contribute nothing at all.
                image.data[at] = 128;
                image.data[at + 1] = 128;
                image.data[at + 2] = 0;
                image.data[at + 3] = 0;
                continue;
            }

            // Zero at the centre and at the rim, peaking half way out. At the centre the unit
            // vector is undefined, which does not matter — the slope there is zero anyway.
            const slope = Math.sin(spread * Math.PI);
            const unit = spread > 0 ? 1 / (spread * radius) : 0;

            image.data[at] = Math.round(128 + acrossX * unit * slope * 127);
            image.data[at + 1] = Math.round(128 + acrossY * unit * slope * 127);
            image.data[at + 2] = 0;
            image.data[at + 3] = Math.round((1 - spread * spread) ** LENS_FEATHER * 255);
        }
    }

    context.putImageData(image, 0, 0);

    lens = Texture.from(canvas);
    return lens;
}

/**
 * One mark on the water. Positions are in **view space** — the coordinates the field is drawn in,
 * whose origin is the top-left of the viewport — and are slid by `scroll` as the sea goes past,
 * the same way the school's are.
 */
interface Mark {
    /** What the mark looks like: a blob of foam, additive, in the map's blue channel. */
    foam: Sprite;
    /**
     * What the mark does to the water beneath it, in red and green. `null` when the warp is
     * dialled off, in which case those channels are never read and there is nothing to draw.
     */
    lens: Sprite | null;
    x: number;
    y: number;
    /** Its own drift, in view pixels per second. Perpendicular to the track: this opens the V. */
    driftX: number;
    driftY: number;
    /** Which way the mark is stretched. Fixed at birth — foam does not turn once it is down. */
    rotation: number;
    age: number;
    /** Strength at birth, before the fade. Turbo lays a stronger mark; see `_lay`. */
    weight: number;
    /** Width at birth and at death, in view pixels. Turbo lays a wider mark. */
    widthStart: number;
    widthEnd: number;
}

/** Where one hull's trail has got to. One per boat this client is drawing. */
interface Track {
    /** The last point a pair of marks was laid at, in view space. Scrolled with the marks. */
    x: number;
    y: number;
    /** Seconds since this hull last reported in. Tracks for boats that left are pruned. */
    idle: number;
}

/**
 * The trail a hull leaves **on the water**: two lines of foam laid at the stern, which then
 * drift apart and fade where they were put, and a bend in the sea under them.
 *
 * **This is the only wake there is.** A hull used to carry one of its own as well — a foam sprite
 * welded to the transom, turning with the ship — and it was removed once this could do the whole
 * job; see design/Ships.md. Nothing here is attached to the hull that made it: once a mark is
 * laid it belongs to the sea, and the ship sails out of it.
 *
 * **Decoration only**, like the school in [SeaLife.ts](SeaLife.ts): the server does not know
 * it exists, nothing about it is on the wire, and two clients watching the same battle draw
 * different foam. A trail is a picture of a course the arena has already decided.
 *
 * **Nothing here is in the scene graph.** `field` is rendered on its own into a texture that
 * [OceanFilter.ts](OceanFilter.ts) draws the whole sea through, so a mark is never a sprite laid over
 * the water — it is part of how the water itself is drawn. That is what lets foam sit on top of a
 * warp it is causing without being dragged around by it. See design/Water.md.
 *
 * Marks are laid by **distance covered**, not by the clock. That is what makes a boat sitting
 * still leave nothing behind it and a boat under turbo lay the same trail faster, with no
 * special case for either.
 *
 * The pool is a **ring**. `budget` marks are made once and never allocated again; because every
 * mark is given the same life, they expire in the order they were laid, so the oldest is always
 * at the head of the ring and a full sea can take it back in constant time.
 */
export class WakeField {
    private readonly _field = new Container();
    /**
     * The two halves of a mark, in two layers, and the order is load-bearing. A lens blends
     * normally, so one drawn *after* a foam blob would take that blob's blue back out with it;
     * foam is additive and only touches blue, so it can never disturb a lens. Everything that
     * pushes the water goes down first, and all the foam goes on top.
     */
    private readonly _lenses = new Container();
    private readonly _foam = new Container();
    private readonly _marks: Mark[] = [];
    private readonly _tracks = new Map<string, Track>();

    /** Index of the oldest live mark in the ring, and how many are live from there. */
    private _head = 0;
    private _count = 0;

    private _width: number;
    private _height: number;

    /**
     * The trail as one image, for `Water.setTrail` to render and draw the sea through. Never part
     * of the scene: red and green in it are which way the water is pushed and blue is how much
     * foam is on it, which is a thing to be read rather than a thing to be looked at.
     */
    public get field(): Container {
        return this._field;
    }

    /** How hard the trail bends the water under it, in pixels. Zero leaves foam and nothing else. */
    public get displacementStrength(): number {
        return DISPLACEMENT;
    }

    /** How solid the foam reads where the filter decides there is any, 0 to 1. */
    public get foamStrength(): number {
        return OPACITY;
    }

    /**
     * Puts the field at the camera's zoom. A mark is measured against a hull, so it has to shrink
     * with the hulls — see design/Water.md.
     */
    public setZoom(zoom: number): void {
        this._field.scale.set(zoom);
    }

    constructor(width: number, height: number) {
        this._width = width;
        this._height = height;

        this._field.addChild(this._lenses, this._foam);

        this.restock();
    }

    /**
     * Rebuilds the pool of marks to the current `budget`, and to whether the warp is dialled on at
     * all. The trail on the water is dropped with it.
     *
     * Only the dashboard calls it — see `setWakeDials`. In a battle the pool is built once and
     * recycled forever. Everything is remade rather than trimmed because the ring's indices mean
     * nothing once its length has moved, and a trail that has to be re-laid is a second of sailing.
     */
    public restock(): void {
        for (const mark of this._marks) {
            mark.foam.destroy();
            mark.lens?.destroy();
        }

        this._marks.length = 0;
        this._head = 0;
        this._count = 0;
        this._tracks.clear();

        const blob = BUDGET > 0 ? foamTexture() : Texture.EMPTY;
        // Only when the warp is actually dialled on: at zero those two channels are never read,
        // so there is no reason to build the lens texture or to carry a second sprite per mark.
        const bump = BUDGET > 0 && DISPLACEMENT > 0 ? lensTexture() : null;

        for (let index = 0; index < BUDGET; index++) {
            // Pure blue and additive, so a mark adds to the foam channel and cannot touch the two
            // that steer the water. Overlapping marks build up rather than covering each other.
            const foamSprite = new Sprite({ texture: blob, anchor: 0.5, tint: 0x0000ff, blendMode: "add", visible: false });
            this._foam.addChild(foamSprite);

            let lensSprite: Sprite | null = null;
            if (bump) {
                lensSprite = new Sprite({ texture: bump, anchor: 0.5, visible: false });
                this._lenses.addChild(lensSprite);
            }

            this._marks.push({
                foam: foamSprite,
                lens: lensSprite,
                x: 0,
                y: 0,
                driftX: 0,
                driftY: 0,
                rotation: 0,
                age: 0,
                weight: FOAM_WEIGHT,
                widthStart: WIDTH_START,
                widthEnd: WIDTH_END
            });
        }
    }

    /**
     * Reports where one hull is this frame, and lays however much foam it has earned since the
     * last report. Called for **every** boat on screen, the local one included.
     *
     * `x`/`y` are the hull's centre in view space — the same coordinates the boat itself is
     * drawn at — so the caller never has to know that the seam or the camera exist. `length`
     * and `beam` are the hull's, because the stern offset and the width of the V are measured
     * in hulls rather than in pixels: a galleon's trail has to leave a galleon's stern.
     */
    public trail(id: string, x: number, y: number, heading: number, length: number, beam: number, boosting: boolean): void {
        if (BUDGET <= 0 || LIFE <= 0) return;

        // Heading 0 is north, so the bow is (sin, -cos) and the stern is the other way.
        const sternX = x - Math.sin(heading) * length * STERN_OFFSET;
        const sternY = y + Math.cos(heading) * length * STERN_OFFSET;

        let track = this._tracks.get(id);
        if (!track) {
            track = { x: sternX, y: sternY, idle: 0 };
            this._tracks.set(id, track);
            // Nothing laid on the first report: there is no previous point to measure from,
            // and treating the origin as one would draw a line in from the corner.
            return;
        }

        track.idle = 0;

        let deltaX = sternX - track.x;
        let deltaY = sternY - track.y;
        let gap = Math.hypot(deltaX, deltaY);

        // A jump this big is not a fast frame — it is a respawn, or a hull that has just come
        // into view. Pick the track up where the ship actually is and lay nothing across it.
        if (gap > SPACING * MAX_CATCHUP) {
            track.x = sternX;
            track.y = sternY;
            return;
        }

        // Stepped along the chord rather than laid at the stern, so the spacing is even
        // whatever the frame rate did — a long frame leaves a line, not a gap and a blob.
        while (gap >= SPACING) {
            const alongX = deltaX / gap;
            const alongY = deltaY / gap;

            track.x += alongX * SPACING;
            track.y += alongY * SPACING;

            this._lay(track.x, track.y, alongX, alongY, beam, boosting);

            deltaX = sternX - track.x;
            deltaY = sternY - track.y;
            gap = Math.hypot(deltaX, deltaY);
        }
    }

    public update(deltaSeconds: number): void {
        // Every mark is given the same life, so they die in the order they were laid and the
        // dead are always a run at the head of the ring.
        while (this._count > 0 && this._marks[this._head].age >= LIFE) {
            this._hide(this._marks[this._head]);
            this._head = (this._head + 1) % BUDGET;
            this._count--;
        }

        for (let step = 0; step < this._count; step++) {
            const mark = this._marks[(this._head + step) % BUDGET];

            mark.age += deltaSeconds;
            mark.x += mark.driftX * deltaSeconds;
            mark.y += mark.driftY * deltaSeconds;
            this._draw(mark);
        }

        for (const [id, track] of this._tracks) {
            track.idle += deltaSeconds;
            // Gone for longer than a trail lasts, so there is nothing of this hull left on the
            // water to be continuous with. Anything else would leak a track per ship per battle.
            if (track.idle > LIFE) this._tracks.delete(id);
        }
    }

    /**
     * Slides every mark by a world offset, exactly as `SeaLife.scroll` slides the school. The
     * trail is *in* the water, so a boat sailing east has to leave it astern — without this the
     * whole thing would hang off the camera and follow the player around.
     *
     * The tracks move with it, because a track is a point on the water like any other: leave it
     * behind and the next frame's gap is measured from a place the sea has already left.
     */
    public scroll(deltaX: number, deltaY: number): void {
        for (let step = 0; step < this._count; step++) {
            const mark = this._marks[(this._head + step) % BUDGET];
            mark.x -= deltaX;
            mark.y -= deltaY;
        }

        for (const track of this._tracks.values()) {
            track.x -= deltaX;
            track.y -= deltaY;
        }
    }

    public resize(width: number, height: number): void {
        this._width = width;
        this._height = height;
    }

    public destroy(): void {
        // By hand, because this was never in the scene graph — nothing else will take it.
        this._field.destroy({ children: true });
    }

    /**
     * Lays one pair of marks across the track: one to port, one to starboard.
     *
     * A pair rather than a single mark down the centreline, because two lines opening astern is
     * the shape a hull actually leaves and one fat stripe reads as a smear. `along` is the unit
     * direction the ship is moving through the water — the chord between this mark and the last
     * one, not the bow — so the V is square to the track it is marking.
     */
    private _lay(x: number, y: number, alongX: number, alongY: number, beam: number, boosting: boolean): void {
        const acrossX = -alongY;
        const acrossY = alongX;
        const offset = beam * ARM_SPREAD;

        const widthFactor = boosting ? BOOST_WIDTH : 1;
        // Capped, because it leaves here as a sprite's alpha. FOAM_WEIGHT sits well below 1
        // precisely so that there is room above it for this.
        const weight = Math.min(1, FOAM_WEIGHT * (boosting ? BOOST_OPACITY : 1));
        // Symmetric about the track: local -y is forward for a hull, so it is here too. The
        // blob has no front, so which end of the axis this picks does not matter.
        const rotation = Math.atan2(alongX, -alongY);

        for (const side of SIDES) {
            const mark = this._claim();
            if (!mark) return;

            // Scattered off the exact point, and rolled a little either side of the dialled
            // size. Both are what stop the arm reading as a drawn line — see JITTER.
            const scatter = WIDTH_START * JITTER;
            const size = 1 + (Math.random() * 2 - 1) * SIZE_VARIANCE;

            mark.x = x + acrossX * offset * side + (Math.random() * 2 - 1) * scatter;
            mark.y = y + acrossY * offset * side + (Math.random() * 2 - 1) * scatter;
            mark.driftX = acrossX * SPREAD_RATE * side;
            mark.driftY = acrossY * SPREAD_RATE * side;
            mark.rotation = rotation;
            mark.age = 0;
            mark.weight = weight;
            mark.widthStart = WIDTH_START * widthFactor * size;
            mark.widthEnd = WIDTH_END * widthFactor * size;

            // Drawn on the spot rather than waiting for `update`, so the order the scene calls
            // the two in cannot leave a fresh mark showing last life's position for a frame.
            this._draw(mark);
        }
    }

    /**
     * The next mark to write into. Once the ring is full this is the **oldest** one, taken back
     * mid-life: a sea with more hulls in it than the budget covers loses the tails of the
     * longest trails, which is the failure nobody looks at, rather than the frame rate.
     */
    private _claim(): Mark | null {
        if (BUDGET <= 0) return null;

        if (this._count === BUDGET) {
            this._head = (this._head + 1) % BUDGET;
            this._count--;
        }

        const mark = this._marks[(this._head + this._count) % BUDGET];
        this._count++;
        return mark;
    }

    /** Puts one mark's sprites where its age says they should be, and hides them out of shot. */
    private _draw(mark: Mark): void {
        const onScreen =
            mark.x > -CULL_MARGIN && mark.x < this._width + CULL_MARGIN && mark.y > -CULL_MARGIN && mark.y < this._height + CULL_MARGIN;

        if (!onScreen) {
            this._hide(mark);
            return;
        }

        const spent = Math.min(1, mark.age / LIFE);
        const fade = (1 - spent) ** FADE_POWER;
        const width = mark.widthStart + (mark.widthEnd - mark.widthStart) * spent;

        mark.foam.visible = true;
        mark.foam.position.set(mark.x, mark.y);
        mark.foam.rotation = mark.rotation;
        mark.foam.alpha = mark.weight * fade;
        // Stretched along the track, across the blob's own axes — see ELONGATION.
        mark.foam.scale.set(width / BLOB_SIZE, (width * ELONGATION) / BLOB_SIZE);

        if (!mark.lens) return;

        // The same mark, wider: the disturbance in the water reaches further than the white of
        // it. It fades on the same clock, because it is the same mark dying.
        const reach = width * LENS_SPREAD;

        mark.lens.visible = true;
        mark.lens.position.set(mark.x, mark.y);
        mark.lens.rotation = mark.rotation;
        mark.lens.alpha = fade;
        mark.lens.scale.set(reach / LENS_SIZE, (reach * ELONGATION) / LENS_SIZE);
    }

    /** Takes a mark off the map, whether it is spent or merely out of shot. */
    private _hide(mark: Mark): void {
        mark.foam.visible = false;
        if (mark.lens) mark.lens.visible = false;
    }
}
